import React, { FC } from 'react';
import {
  AddressType,
  GetPolicyByVersionIdResponse,
  LienholderType,
  VehicleType,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import { Container } from '@clearcover/atlas';
import { CardSectionInfoPairs, LoadingPage } from 'Shared/components';
import styles from './styles.module.css';
import {
  booleanValueMapping,
  EMDASH,
  formatAddress,
  formatNumberWithCommas,
  isPresent,
} from 'Shared/utils';
import { MultilineAddress } from 'Policies/components/Address';
import { useNewApi } from 'Shared/services/Api';
import { useParams } from 'react-router-dom';
import { areSameAddress } from 'Policies/utils/helpers';
import { formatDiff } from 'Policies/components/PolicyActivities/Common/Common';

type VehicleDetailsDiff = {
  diff: VersionedObjectDiff<VehicleType>;
  versionId: string;
};

export const PolicyChangeVehicleDetails: FC<VehicleDetailsDiff> = ({
  diff,
  versionId,
}) => {
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const [{ data, loading }] = useNewApi<GetPolicyByVersionIdResponse>(
    {
      url: `/policies/${policyId}/version/${versionId}`,
    },
    true,
    false
  );

  if (loading) {
    return <LoadingPage />;
  }

  const VehicleToShowDefault =
    diff.type === 'ADDED' || diff.type === 'UPDATED' ? diff.new : diff.old;
  const vehicleToDiff =
    diff.type === 'UPDATED' ? diff.old : VehicleToShowDefault;

  const mailingAddress = data?.policyDetails.addresses.mailing;

  return (
    <div data-testid="vehicle-details">
      <Container>
        <h4>Vehicle Info</h4>
        <CardSectionInfoPairs
          headers={[
            'VIN',
            'Year',
            'Make',
            'Model',
            'Body Style',
            'Anti-Theft Features',
          ]}
          data={[
            formatDiff(
              vehicleToDiff.vin.toUpperCase(),
              VehicleToShowDefault.vin.toUpperCase()
            ),
            formatDiff(vehicleToDiff.year, VehicleToShowDefault.year),
            formatDiff(vehicleToDiff.make, VehicleToShowDefault.make),
            formatDiff(vehicleToDiff.model, VehicleToShowDefault.model),
            formatDiff(vehicleToDiff.bodyStyle, VehicleToShowDefault.bodyStyle),
            formatDiff(
              antiTheftString(vehicleToDiff.antiTheft),
              antiTheftString(VehicleToShowDefault.antiTheft)
            ),
          ]}
          className={styles.details}
        />
      </Container>
      <Container>
        <h4>Details</h4>
        <CardSectionInfoPairs
          headers={[
            'Avg Miles Driven Per Year',
            'Rideshare',
            'Primary Use',
            'Registered to applicant/spouse?',
            'Ownership Status',
            'Lienholder Name',
            'Lienholder Address',
          ]}
          data={[
            formatDiff(
              formatWithCommas(vehicleToDiff.usage.yearlyMileage),
              formatWithCommas(VehicleToShowDefault.usage.yearlyMileage)
            ),
            formatDiff(
              formatYesNo(vehicleToDiff.usage.rideShare),
              formatYesNo(VehicleToShowDefault.usage.rideShare)
            ),
            formatDiff(vehicleToDiff.usage.use, VehicleToShowDefault.usage.use),
            formatDiff(
              booleanValueMapping[
                vehicleToDiff.registeredToPolicyholderOrSpouse?.toString() || ''
              ],
              booleanValueMapping[
                VehicleToShowDefault.registeredToPolicyholderOrSpouse?.toString() ||
                  ''
              ]
            ),
            formatDiff(
              vehicleToDiff.ownerShipType,
              VehicleToShowDefault.ownerShipType
            ),
            formatDiff(
              vehicleToDiff.lienholder.name,
              VehicleToShowDefault.lienholder.name
            ),
            formatDiff(
              toMultiLineAddress(vehicleToDiff),
              toMultiLineAddress(VehicleToShowDefault)
            ),
          ]}
          className={styles.details}
        />
      </Container>
      <Container className={styles.garagingAddressContainer}>
        <h4>Garaging Address</h4>

        <CardSectionInfoPairs
          headers={[
            'Garaging Address',
            'Is this vehicle parked at the mailing address?',
          ]}
          data={[
            formatDiff(
              {
                display: (
                  <MultilineAddress address={vehicleToDiff.garagingAddress} />
                ),
                value: formatAddress(vehicleToDiff.garagingAddress),
              },
              {
                display: (
                  <MultilineAddress
                    address={VehicleToShowDefault.garagingAddress}
                  />
                ),
                value: formatAddress(VehicleToShowDefault.garagingAddress),
              }
            ),
            formatDiff(
              isSameAsMailingAddress(
                vehicleToDiff.garagingAddress,
                mailingAddress
              ),
              isSameAsMailingAddress(
                VehicleToShowDefault.garagingAddress,
                mailingAddress
              )
            ),
          ]}
          className={styles.details}
        />
      </Container>
    </div>
  );
};

// TODO terms taken from VehicleInfo.tsx, may want to consolidate into a config of some sort
const toAntiTheftString = (input: string): string => {
  switch (input) {
    case 'activeDisable':
      return 'Active Disable';
    case 'alarm':
      return 'Alarm System';
    case 'passiveDisable':
      return 'Passive Disable';
    case 'recovery':
      return 'Vehicle Recovery';
    default:
      return '';
  }
};

const toAddressType = (lienholder: LienholderType): AddressType => {
  return {
    address1: lienholder.addressLine1,
    address2: lienholder.addressLine2,
    city: lienholder.city,
    state: lienholder.state,
    zip: lienholder.postalCode,
  };
};

const formatWithCommas = (number: number | null): string =>
  number ? formatNumberWithCommas(number) : '';

const formatYesNo = (boolValue: boolean): string => (boolValue ? 'Yes' : 'No');

const antiTheftString = (antiTheft: any): string => {
  const antiTheftString = Object.entries(antiTheft)
    .map(([key, value]) => (value ? toAntiTheftString(key) : undefined))
    .filter(value => value !== undefined)
    .join(', ');
  return antiTheftString ? antiTheftString : EMDASH;
};

const toMultiLineAddress = (vechile: VehicleType): any =>
  vechile.ownerShipType === 'owned'
    ? EMDASH
    : {
        display: (
          <MultilineAddress address={toAddressType(vechile.lienholder)} />
        ),
        value: vechile.lienholder.addressFull,
      };

const isSameAsMailingAddress = (
  garagingAddress: AddressType,
  mailingAddress: AddressType
): string =>
  isPresent(mailingAddress)
    ? formatYesNo(areSameAddress(garagingAddress, mailingAddress))
    : EMDASH;
